import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Image } from 'app/model/entities/entity-model';
import { Entity, EntityState } from 'breeze-client';
import _ from 'lodash';
import { DialogService } from 'primeng/dynamicdialog';
import { DbQueryService } from '../db-query.service';
import { DbSaveService } from '../db-save.service';
import { DialogBaseComponent } from '../dialog-base.component';
import { FileGalleryDialogComponent, FileGalleryDialogParams } from './file-gallery-dialog.component';
import { AppImage, ImageFns } from './image-fns';

/** Implemented by AddonTypeImage, ProductTypeImage, ManufacturerImage */
interface RelatedImage extends Entity {
  imageId: number;
  image: Image;
  isPrimary: boolean;
  isThumbnail: boolean;
}

/** Handles image uploads and interactions with image gallery */
@Component({
  selector: 'app-image-handler',
  template: `
    <button *ngIf="!hideButton" (click)="openFileViewDialog()" [disabled]="isReadOnly"
      pButton type="button" class="p-button-raised" icon="pi pi-image"
    > Add / View {{ imageCount }} Images</button>
  `,
  standalone: false
})
export class ImageHandlerComponent {
  /** Linking entity collection on the parent entity, e.g. manufacturer.manufacturerImages */
  @Input() oldImages!: RelatedImage[];
  @Input() isReadOnly = false;
  /** Whether to hide the openFileDialog button.  If true, then the parent component must call openFileDialog() directly. */
  @Input() hideButton = false;
  /** Create the join entity between Image and parent entity.  Example implementation:
   * this.dbSaveService.createEntity(ManufacturerImage, {
   *   manufacturerId: this.manufacturer.id,
   *   imageId: event.imageId,
   *   isPrimary: event.isPrimary,
   *   isThumbnail: event.isThumbnail
   * });
   */
  @Output() createJoinEntity = new EventEmitter<{
    imageId: number;
    isPrimary: boolean;
    isThumbnail: boolean;
  }>();

  newImages: AppImage[] = [];

  constructor(
    private dbQueryService: DbQueryService,
    private dbSaveService: DbSaveService,
    public pngDialogService: DialogService
  ) { }

  /** Images that are not deleted or detached */
  get filteredNewImages(): ReadonlyArray<AppImage> {
    return this.newImages.filter(
      (x) =>
        !x.entityAspect ||
        (x.entityAspect.entityState != EntityState.Deleted &&
          x.entityAspect.entityState != EntityState.Detached)
    );
  }

  get imageCount(): number {
    // files just added + files already attached to this product type
    return this.filteredNewImages.length + this.oldImages.length;
  }

  async openFileViewDialog(enableSave = false) {
    if (this.oldImages.some((pti) => pti.image == null)) {
      // load the Images
      const imageIds = this.oldImages.map((pti) => pti.imageId);
      await this.dbQueryService.getImages(...imageIds);
    }
    const images = this.oldImages.map((pti) => {
      const image = pti.image as AppImage;
      // image.isPrimary = pti.isPrimary;
      image.isThumbnail = pti.isThumbnail;
      return image;
    });
    this.newImages = this.filteredNewImages.slice();

    const data: FileGalleryDialogParams = {
      newImages: this.newImages,
      oldImages: images,
      onDelete: this.deleteFile.bind(this),
      onChange: this.fileChange.bind(this),
      enableSave,
    };

    return await DialogBaseComponent.open(FileGalleryDialogComponent, this, data);
  }

  /** Create Images from the AppImages, and create join entities for each */
  async attachFiles() {
    for (const img of this.filteredNewImages) {
      const si = await ImageFns.makeImageEntity(img, this.dbSaveService.uow);
      const re = this.createJoinEntity.emit({
        imageId: si.id,
        isPrimary: img.isPrimary || false,
        isThumbnail: img.isThumbnail || false,
      });
    }

    this.newImages = [];
  }

  /** When an image is deleted from the gallery dialog, remove only this parent's
   *  link to the image. The Image itself (and any other join rows pointing at it
   *  from other parents) is left intact, so shared images survive. */
  private deleteFile(image: AppImage) {
    if (image instanceof Image) {
      const join = this.oldImages.find(pti => pti.imageId === image.id);
      join?.entityAspect.setDeleted();
    } else if (image.file) {
      _.remove(this.newImages, (f) => f === image);
    }
  }

  /** When an image is added or changed in the gallery dialog, change it here */
  private fileChange(image: AppImage) {
    if (image instanceof Image) {
      const pti = this.oldImages.find((pti) => pti.image == image);
      if (pti) {
        pti.isPrimary = !!(image as AppImage).isPrimary;
      }
    }
  }

}
